import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
interface QuestShardProps {
  onCollect: () => void;
  /** Edge length in px */
  size?: number;
  /** Clickable yet? (hidden shards sit under things until uncovered) */
  active?: boolean;
  className?: string;
  style?: React.CSSProperties;
}
const EASE = [0.23, 1, 0.32, 1] as const;
/** A fragment of Pixel's LED face: lit cells of a 3×3 grid */
const LIT = [1, 0, 1, 1, 1, 0, 0, 1, 1];

/**
 * One shard of Pixel's memory core, drawn in the site's language: a black tile like the buttons,
 * holding a 3×3 fragment of his pixel face in brand blue, with a quiet beacon outline so it reads as
 * "pick me up" on white, blue or black.
 */
export function QuestShard({
  onCollect,
  size = 44,
  active = true,
  className = '',
  style
}: QuestShardProps) {
  const reduce = useReducedMotion();
  return <motion.button type="button"
  // Lets the site-wide link handler know this click is for the shard, not the link it sits inside
  data-quest-shard="" aria-label="Memory shard: pick it up" disabled={!active} onClick={e => {
    e.preventDefault();
    e.stopPropagation();
    onCollect();
  }} className={`relative grid place-items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#1A2FFB] disabled:cursor-default ${className}`} style={{
    width: size,
    height: size,
    ...style
  }} initial={reduce ? false : {
    opacity: 0,
    scale: 0.96
  }} animate={{
    opacity: 1,
    scale: 1
  }} exit={{
    opacity: 0,
    scale: 0.96,
    transition: {
      duration: 0.16,
      ease: EASE
    }
  }} transition={{
    duration: 0.24,
    ease: EASE
  }} whileHover={reduce || !active ? undefined : {
    scale: 1.06,
    transition: {
      duration: 0.16,
      ease: EASE
    }
  }} whileTap={reduce || !active ? undefined : {
    scale: 0.94,
    transition: {
      duration: 0.1,
      ease: EASE
    }
  }} data-id="element-255" data-mp="3vl1fp6empwwu">
      {!reduce && <motion.span aria-hidden="true" className="absolute inset-0 rounded-[28%] border-2 border-[#1A2FFB]" animate={{
      scale: [1, 1.6],
      opacity: [0.8, 0]
    }} transition={{
      duration: 1.6,
      ease: [0.23, 1, 0.32, 1],
      repeat: Infinity
    }} data-id="element-256" data-mp="371oqxz37lobh" />}
      <span aria-hidden="true" className="relative grid h-full w-full grid-cols-3 gap-[8%] rounded-[28%] bg-black p-[20%] ring-1 ring-white/30" data-id="element-257" data-mp="nyax7jgyoy77">
        {LIT.map((on, i) => <motion.span key={i} className={`rounded-[1px] ${on ? 'bg-[#1A2FFB]' : 'bg-white/15'}`} animate={reduce || !on ? undefined : {
        opacity: [1, 0.45, 1]
      }} transition={{
        duration: 1.4,
        ease: 'easeInOut',
        repeat: Infinity,
        delay: i * 0.09
      }} data-id="element-258" data-mp="3rft7dxk7ihas" data-mpi={i} />)}
      </span>
    </motion.button>;
}